import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { TextFieldModule } from '@angular/cdk/text-field';
import {
  SectionSpec,
  TextSection,
  ListSection,
  CardSection,
  TableSection,
  ListItem
} from '../../../core/interfaces/report-spec.interface';

/**
 * Recursive Section Editor Component
 *
 * Provides a simple tree-like editor for report sections.
 * Each section type has its own form fields.
 */
@Component({
  selector: 'app-section-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    TextFieldModule
  ],
  template: `
    <mat-expansion-panel class="mb-2" [expanded]="expanded">
      <mat-expansion-panel-header>
        <mat-panel-title class="flex items-center gap-2">
          <mat-icon class="text-gray-500">{{ getTypeIcon(section.type) }}</mat-icon>
          <span class="font-medium">{{ getSectionTitle() }}</span>
          <span class="text-xs text-gray-400 ml-2">({{ section.type }})</span>
        </mat-panel-title>
        <mat-panel-description class="flex items-center">
          <button mat-icon-button (click)="onMoveUp($event)" [disabled]="isFirst" class="scale-75">
            <mat-icon>arrow_upward</mat-icon>
          </button>
          <button mat-icon-button (click)="onMoveDown($event)" [disabled]="isLast" class="scale-75">
            <mat-icon>arrow_downward</mat-icon>
          </button>
          <button mat-icon-button color="warn" (click)="onDelete($event)" class="scale-75">
            <mat-icon>delete</mat-icon>
          </button>
        </mat-panel-description>
      </mat-expansion-panel-header>

      <div class="p-2">
        <!-- Type Selector -->
        <mat-form-field appearance="outline" class="w-full mb-2">
          <mat-label>Section Type</mat-label>
          <mat-select [(ngModel)]="section.type" (ngModelChange)="onTypeChange($event)">
            <mat-option value="text">Text</mat-option>
            <mat-option value="list">List</mat-option>
            <mat-option value="card">Card</mat-option>
            <mat-option value="table">Table</mat-option>
          </mat-select>
        </mat-form-field>

        <!-- TEXT Section Editor -->
        <ng-container *ngIf="section.type === 'text'">
          <mat-form-field appearance="outline" class="w-full mb-2">
            <mat-label>Variant</mat-label>
            <mat-select [(ngModel)]="asText(section).variant" (ngModelChange)="emitChange()">
              <mat-option value="title">Title</mat-option>
              <mat-option value="heading">Heading</mat-option>
              <mat-option value="body">Body</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Content (Markdown)</mat-label>
            <textarea matInput
                      cdkTextareaAutosize
                      cdkAutosizeMinRows="3"
                      [(ngModel)]="asText(section).content"
                      (ngModelChange)="emitChange()">
            </textarea>
          </mat-form-field>
        </ng-container>

        <!-- LIST Section Editor -->
        <ng-container *ngIf="section.type === 'list'">
          <div class="mb-2 text-sm text-gray-500">
            Items can be simple text (bullet list) or have label/value (field list)
          </div>
          <div *ngFor="let item of asList(section).items; let i = index" class="flex gap-2 mb-2 items-start">
            <div class="flex-1">
              <ng-container *ngIf="isLabeledItem(item); else simpleItem">
                <div class="flex gap-2">
                  <mat-form-field appearance="outline" class="w-1/3">
                    <mat-label>Label</mat-label>
                    <input matInput [(ngModel)]="asLabeledItem(item).label" (ngModelChange)="emitChange()">
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="flex-1">
                    <mat-label>Value</mat-label>
                    <input matInput [(ngModel)]="asLabeledItem(item).value" (ngModelChange)="emitChange()">
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="w-1/4">
                    <mat-label>Metadata</mat-label>
                    <input matInput [(ngModel)]="asLabeledItem(item).metadata" (ngModelChange)="emitChange()">
                  </mat-form-field>
                </div>
              </ng-container>
              <ng-template #simpleItem>
                <mat-form-field appearance="outline" class="w-full">
                  <mat-label>Item {{ i + 1 }}</mat-label>
                  <input matInput [ngModel]="item" (ngModelChange)="updateSimpleItem(i, $event)">
                </mat-form-field>
              </ng-template>
            </div>
            <button mat-icon-button color="warn" (click)="removeListItem(i)" class="mt-2">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="flex gap-2">
            <button mat-stroked-button (click)="addSimpleListItem()">
              <mat-icon>add</mat-icon> Add Bullet
            </button>
            <button mat-stroked-button (click)="addLabeledListItem()">
              <mat-icon>add</mat-icon> Add Field
            </button>
          </div>
        </ng-container>

        <!-- CARD Section Editor -->
        <ng-container *ngIf="section.type === 'card'">
          <mat-form-field appearance="outline" class="w-full mb-4">
            <mat-label>Card Title</mat-label>
            <input matInput [(ngModel)]="asCard(section).title" (ngModelChange)="emitChange()">
          </mat-form-field>

          <div class="pl-4 border-l-2 border-blue-300">
            <div class="text-sm text-gray-500 mb-2">Card Content (nested sections)</div>
            <div *ngFor="let child of asCard(section).content; let i = index">
              <app-section-editor
                [section]="child"
                [isFirst]="i === 0"
                [isLast]="i === asCard(section).content.length - 1"
                (sectionChange)="onChildChange()"
                (delete)="removeChildSection(i)"
                (moveUp)="moveChildUp(i)"
                (moveDown)="moveChildDown(i)">
              </app-section-editor>
            </div>
            <button mat-stroked-button color="primary" (click)="addChildSection()" class="mt-2">
              <mat-icon>add</mat-icon> Add Nested Section
            </button>
          </div>
        </ng-container>

        <!-- TABLE Section Editor -->
        <ng-container *ngIf="section.type === 'table'">
          <mat-form-field appearance="outline" class="w-full mb-2">
            <mat-label>Table Title</mat-label>
            <input matInput [(ngModel)]="asTable(section).title" (ngModelChange)="emitChange()">
          </mat-form-field>

          <div class="mb-4">
            <div class="text-sm text-gray-500 mb-2">Columns</div>
            <div class="flex gap-2 flex-wrap mb-2">
              <mat-form-field appearance="outline" *ngFor="let col of asTable(section).columns; let i = index" class="w-32">
                <mat-label>Col {{ i + 1 }}</mat-label>
                <input matInput [ngModel]="col" (ngModelChange)="updateColumn(i, $event)">
                <button mat-icon-button matSuffix (click)="removeColumn(i)" *ngIf="asTable(section).columns.length > 1">
                  <mat-icon>close</mat-icon>
                </button>
              </mat-form-field>
            </div>
            <button mat-stroked-button (click)="addColumn()">
              <mat-icon>add</mat-icon> Add Column
            </button>
          </div>

          <div>
            <div class="text-sm text-gray-500 mb-2">Rows</div>
            <div *ngFor="let row of asTable(section).rows; let rowIndex = index" class="flex gap-2 mb-2 items-center">
              <mat-form-field appearance="outline" *ngFor="let cell of row; let colIndex = index" class="flex-1">
                <input matInput [ngModel]="cell" (ngModelChange)="updateCell(rowIndex, colIndex, $event)">
              </mat-form-field>
              <button mat-icon-button color="warn" (click)="removeRow(rowIndex)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <button mat-stroked-button (click)="addRow()">
              <mat-icon>add</mat-icon> Add Row
            </button>
          </div>
        </ng-container>
      </div>
    </mat-expansion-panel>
  `,
  styles: [`
    :host {
      display: block;
    }
    mat-expansion-panel {
      background: #fafafa;
    }
  `]
})
export class SectionEditorComponent {
  @Input() section!: SectionSpec;
  @Input() isFirst = false;
  @Input() isLast = false;
  @Input() expanded = true;

  @Output() sectionChange = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() moveUp = new EventEmitter<void>();
  @Output() moveDown = new EventEmitter<void>();

  getTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      text: 'text_fields',
      list: 'list',
      card: 'dashboard',
      table: 'table_chart'
    };
    return icons[type] || 'help';
  }

  getSectionTitle(): string {
    switch (this.section.type) {
      case 'text':
        const text = (this.section as TextSection).content;
        return text.substring(0, 30) + (text.length > 30 ? '...' : '');
      case 'card':
        return (this.section as CardSection).title || 'Untitled Card';
      case 'table':
        return (this.section as TableSection).title || 'Untitled Table';
      case 'list':
        return `List (${(this.section as ListSection).items.length} items)`;
      default:
        return 'Section';
    }
  }

  // Type conversion helpers
  asText(s: SectionSpec): TextSection { return s as TextSection; }
  asList(s: SectionSpec): ListSection { return s as ListSection; }
  asCard(s: SectionSpec): CardSection { return s as CardSection; }
  asTable(s: SectionSpec): TableSection { return s as TableSection; }

  isLabeledItem(item: ListItem): boolean {
    return typeof item !== 'string' && item.label !== undefined;
  }

  asLabeledItem(item: ListItem): { label?: string; value: string; metadata?: string } {
    return item as { label?: string; value: string; metadata?: string };
  }

  emitChange(): void {
    this.sectionChange.emit();
  }

  onTypeChange(newType: string): void {
    // Reset section data when type changes
    const baseSection = { type: newType } as any;

    switch (newType) {
      case 'text':
        baseSection.content = '';
        baseSection.variant = 'body';
        break;
      case 'list':
        baseSection.items = [];
        break;
      case 'card':
        baseSection.title = '';
        baseSection.content = [];
        break;
      case 'table':
        baseSection.title = '';
        baseSection.columns = ['Column 1'];
        baseSection.rows = [];
        break;
    }

    Object.assign(this.section, baseSection);
    this.emitChange();
  }

  // List operations
  addSimpleListItem(): void {
    (this.section as ListSection).items.push('New item');
    this.emitChange();
  }

  addLabeledListItem(): void {
    (this.section as ListSection).items.push({ label: 'Label', value: 'Value' });
    this.emitChange();
  }

  updateSimpleItem(index: number, value: string): void {
    (this.section as ListSection).items[index] = value;
    this.emitChange();
  }

  removeListItem(index: number): void {
    (this.section as ListSection).items.splice(index, 1);
    this.emitChange();
  }

  // Card operations
  addChildSection(): void {
    (this.section as CardSection).content.push({
      type: 'text',
      content: 'New text',
      variant: 'body'
    });
    this.emitChange();
  }

  removeChildSection(index: number): void {
    (this.section as CardSection).content.splice(index, 1);
    this.emitChange();
  }

  onChildChange(): void {
    this.emitChange();
  }

  moveChildUp(index: number): void {
    const content = (this.section as CardSection).content;
    if (index > 0) {
      [content[index - 1], content[index]] = [content[index], content[index - 1]];
      this.emitChange();
    }
  }

  moveChildDown(index: number): void {
    const content = (this.section as CardSection).content;
    if (index < content.length - 1) {
      [content[index], content[index + 1]] = [content[index + 1], content[index]];
      this.emitChange();
    }
  }

  // Table operations
  addColumn(): void {
    const table = this.section as TableSection;
    table.columns.push(`Column ${table.columns.length + 1}`);
    table.rows.forEach(row => row.push(''));
    this.emitChange();
  }

  removeColumn(index: number): void {
    const table = this.section as TableSection;
    table.columns.splice(index, 1);
    table.rows.forEach(row => row.splice(index, 1));
    this.emitChange();
  }

  updateColumn(index: number, value: string): void {
    (this.section as TableSection).columns[index] = value;
    this.emitChange();
  }

  addRow(): void {
    const table = this.section as TableSection;
    table.rows.push(table.columns.map(() => ''));
    this.emitChange();
  }

  removeRow(index: number): void {
    (this.section as TableSection).rows.splice(index, 1);
    this.emitChange();
  }

  updateCell(rowIndex: number, colIndex: number, value: string): void {
    (this.section as TableSection).rows[rowIndex][colIndex] = value;
    this.emitChange();
  }

  // Section operations
  onDelete(event: Event): void {
    event.stopPropagation();
    this.delete.emit();
  }

  onMoveUp(event: Event): void {
    event.stopPropagation();
    this.moveUp.emit();
  }

  onMoveDown(event: Event): void {
    event.stopPropagation();
    this.moveDown.emit();
  }
}
